import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  LocationDTO,
  PaginatedLocationsDTO,
} from '@peartherapeutics/weaver-api-client';

import { AxiosResponse } from 'axios';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';

import withSort from 'components/common/Sort';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import Pagination from 'components/common/Pagination';
import AuthorizationComponent from 'components/common/AuthorizationComponent';
import { hasRequiredScope, scopes } from 'utils/scope';

import AddLocationButton from './AddLocationButton';
import LocationsTable from './LocationsTable';
import './styles.scss';

const PER_PAGE = 25;

export interface IOwnProps {
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
  sortBy: (prop: string) => void;
  isLocationWrite: boolean;
  currentUserScopes: string[];
}

export interface IPaginateState {
  nextCursor?: string;
  prevCursor?: string;
  currentPage: number;
  totalRecords: number;
  pageSize?: number;
}
export const retrieveLocations = async (
  payload?: any,
): Promise<AxiosResponse<PaginatedLocationsDTO>> => {
  return WeaverApiClientInstance.getLocations(
    payload.limit,
    payload.nextCursor,
    payload.prevCursor,
    payload.sort,
    payload.sortBy,
  );
};

export const ManageLocations = (props: IOwnProps) => {
  const { sortProp, sortOrder, sortBy, isLocationWrite, currentUserScopes } =
    props;

  const [paginateState, setPaginateState] = useState<IPaginateState>({
    nextCursor: undefined,
    prevCursor: undefined,
    currentPage: 1,
    totalRecords: 0,
    pageSize: undefined,
  });
  const { currentPage, totalRecords, pageSize } = paginateState;
  const [locations, setLocationsState] = useState<LocationDTO[]>([]);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);

  const hasLocationReadScope = useRef(
    hasRequiredScope(currentUserScopes)(scopes.LOCATION_READ),
  );

  const getAllLocations = async (action?: string) => {
    if (!hasLocationReadScope.current) return;

    let { nextCursor, prevCursor } = paginateState;
    const limit = PER_PAGE;
    const sort = sortOrder || undefined;
    const sortBy = sortProp || undefined;
    let pageValue = currentPage;
    if (action === 'next') {
      pageValue += 1;
      nextCursor = nextCursor ? `${nextCursor}` : nextCursor;
      prevCursor = undefined;
    }
    if (action === 'prev') {
      pageValue -= 1;
      prevCursor = prevCursor ? `${prevCursor}` : prevCursor;
      nextCursor = undefined;
    }
    if (action === 'first-page') {
      pageValue = 1;
      nextCursor = undefined;
      prevCursor = undefined;
    }

    let response: Nullable<AxiosResponse<PaginatedLocationsDTO>> = null;
    try {
      setIsLoaded(false);
      response = await retrieveLocations({
        limit,
        nextCursor,
        prevCursor,
        sort,
        sortBy,
      });
    } catch (e) {
      ErrorInterceptor.error(e);
    } finally {
      if (response) {
        setLocationsState(response.data.data);
        setPaginateState({
          nextCursor: response.data.meta?.page.nextCursor ?? undefined,
          prevCursor: response.data.meta?.page.prevCursor ?? undefined,
          currentPage: pageValue,
          totalRecords: response.data.meta!.page.count,
          pageSize: response.data.meta!.page.limit,
        });
        setIsLoaded(true);
      }
    }
  };

  useEffect(() => {
    if (reload) getAllLocations('first-page');
    setReload(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const resetPageState = useCallback(() => {
    setPaginateState({
      prevCursor: undefined,
      nextCursor: undefined,
      currentPage: 1,
      totalRecords: 0,
      pageSize: undefined,
    });
    setReload(!reload);
  }, [reload]);

  useEffect(() => {
    if (!isMounted) {
      getAllLocations();
      setIsMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  useEffect(() => {
    if (isMounted && (sortOrder || sortProp)) {
      resetPageState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortProp, sortOrder]);

  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  return (
    <div data-testid="manage-locations" styleName="manage-locations-wrapper">
      <AuthorizationComponent requiredScope={scopes.LOCATION_WRITE}>
        <div styleName="search-location-wrapper">
          <AddLocationButton getAllLocations={getAllLocations} />
        </div>
      </AuthorizationComponent>
      <AuthorizationComponent requiredScope={scopes.LOCATION_READ}>
        <>
          <div styleName="pagination-container">
            <Pagination
              dataTestId="location-pagination"
              totalRecords={totalRecords}
              onClickNextPage={() => getAllLocations('next')}
              onClickPrevPage={() => getAllLocations('prev')}
              currentPage={currentPage}
              perPage={pageSize ?? PER_PAGE}
              onClickFirstPage={() => getAllLocations('first-page')}
              isLoading={!isLoaded}
            />
          </div>
          <LocationsTable
            locations={locations}
            isLoaded={isLoaded}
            sortProp={sortProp}
            sortOrder={sortOrder}
            sortBy={sortBy}
            isLocationWrite={isLocationWrite}
          />
        </>
      </AuthorizationComponent>
    </div>
  );
};

export default withSort(ManageLocations);
